import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { Clock, Plus, Send, Printer, ChevronLeft, ChevronRight, CheckCircle2, Trash2, AlertCircle, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { usePanel } from "@/context/PanelContext";
import { formatHM, weekdayShort, weekdayShortYear, weekdayOnly, localISODate, parseLocalDate } from "@/lib/utils-tg";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import EditActivityModal from "@/components/EditActivityModal";
import CompanyBadge from "@/components/CompanyBadge";
import Thread from "@/components/Thread";

function isoDate(d) {
  return localISODate(d);
}

function startOfWeek(d) {
  // Sunday-start week — works on a local Date
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = r.getDay();
  r.setDate(r.getDate() - day);
  return r;
}

// Week number using a Sunday-start convention.
// Counts how many Sundays have occurred since the first Sunday on/before Jan 1.
// Uses UTC midnights + rounding so DST transitions don't cause duplicate numbers.
function sundayWeekNumber(d) {
  const sow = startOfWeek(d);
  const year = sow.getFullYear();
  const jan1 = new Date(year, 0, 1);
  const firstSunday = new Date(year, 0, 1 - jan1.getDay()); // first Sunday on/before Jan 1
  const a = Date.UTC(firstSunday.getFullYear(), firstSunday.getMonth(), firstSunday.getDate());
  const b = Date.UTC(sow.getFullYear(), sow.getMonth(), sow.getDate());
  return Math.round((b - a) / (7 * 86400000)) + 1;
}

function formatRange(start, end) {
  const sameMonth = start.getMonth() === end.getMonth();
  const sameYear = start.getFullYear() === end.getFullYear();
  const fmt = (dt, opts) => dt.toLocaleDateString("en-US", opts);
  if (sameMonth && sameYear) {
    return `${fmt(start, { month: "short", day: "numeric" })} – ${fmt(end, { day: "numeric", year: "numeric" })}`;
  }
  if (sameYear) {
    return `${fmt(start, { month: "short", day: "numeric" })} – ${fmt(end, { month: "short", day: "numeric", year: "numeric" })}`;
  }
  return `${fmt(start, { month: "short", day: "numeric", year: "numeric" })} – ${fmt(end, { month: "short", day: "numeric", year: "numeric" })}`;
}

export default function TimesheetPage() {
  const { openNew } = useOutletContext();
  const { refreshKey, triggerRefresh } = usePanel();
  const [activities, setActivities] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [periodInfo, setPeriodInfo] = useState(null);
  const [periodNumber, setPeriodNumber] = useState(null);
  const [period2Data, setPeriod2Data] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem("tg_timesheet_view") || "day");
  useEffect(() => {
    localStorage.setItem("tg_timesheet_view", view);
  }, [view]);
  const [selectedDate, setSelectedDate] = useState(localISODate());
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    api.get("/activities").then((r) => setActivities(r.data));
    api.get("/departments").then((r) => setDepartments(r.data)).catch(() => {});
    api.get("/pay-periods").then((r) => {
      setPeriodInfo(r.data);
      // Keep the user's selected period across refreshes; only default to the
      // current period on first load.
      setPeriodNumber((prev) => (prev == null ? r.data.current.number : prev));
    });
  }, [refreshKey]);

  useEffect(() => {
    if (periodNumber == null) return;
    api.get(`/timesheet/period/${periodNumber}`).then((r) => setPeriod2Data(r.data));
  }, [periodNumber, refreshKey]);

  const catMap = useMemo(() => {
    const m = {};
    departments.forEach((d) => (d.categories || []).forEach((c) => (m[c.id] = { ...c, dept: d.name })));
    return m;
  }, [departments]);
  const deptMap = useMemo(() => Object.fromEntries(departments.map((d) => [d.id, d.name])), [departments]);

  const filteredByView = useMemo(() => {
    if (view === "day") {
      return activities.filter((a) => (a.date || "").slice(0, 10) === selectedDate);
    }
    if (view === "week") {
      const ws = startOfWeek(parseLocalDate(selectedDate));
      const we = new Date(ws);
      we.setDate(we.getDate() + 6);
      const wsIso = localISODate(ws);
      const weIso = localISODate(we);
      return activities.filter((a) => {
        const d = (a.date || "").slice(0, 10);
        return d >= wsIso && d <= weIso;
      });
    }
    return period2Data?.entries || [];
  }, [view, activities, selectedDate, period2Data]);

  const totalMin = filteredByView.reduce((s, a) => s + (a.duration_minutes || 0), 0);

  // Build a rolling list of weeks (current ± 12) and current week value for the Week dropdown
  const { weekOptions, weekValue } = useMemo(() => {
    const ref = parseLocalDate(selectedDate);
    const cur = startOfWeek(ref);
    const opts = [];
    for (let i = 12; i >= -12; i--) {
      const s = new Date(cur);
      s.setDate(s.getDate() - i * 7);
      const e = new Date(s);
      e.setDate(e.getDate() + 6);
      const labelStart = s.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
      const labelEnd = e.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
      opts.push({
        value: isoDate(s),
        number: sundayWeekNumber(s),
        label: `${labelStart} → ${labelEnd}`,
      });
    }
    return { weekOptions: opts, weekValue: isoDate(cur) };
  }, [selectedDate]);

  const submit = async () => {
    if (!periodNumber) return;
    if (period2Data?.entries?.length === 0) {
      toast.error("No entries in this 2-week period");
      return;
    }
    try {
      await api.post("/timesheet/submit", { period_number: periodNumber });
      toast.success("Timesheet submitted to your supervisor");
      triggerRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/activities/${id}`);
      toast.success("Entry deleted");
      triggerRefresh();
    } catch {
      toast.error("Delete failed");
    }
  };

  const submission = period2Data?.submission;
  const periodLabel = period2Data?.period
    ? `Period ${period2Data.period.number} · ${weekdayShortYear(period2Data.period.start)} → ${weekdayShortYear(period2Data.period.end)}`
    : "—";

  return (
    <div className="px-6 md:px-10 pt-6 max-w-6xl mx-auto" data-testid="timesheet-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Timesheet</h1>
          <p className="text-sm text-slate-500 mt-1">Log start/end, dept and category. Submit every 2 weeks for supervisor approval.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => openNew("activity")} className="tg-btn-secondary" data-testid="timesheet-new">
            <Plus className="h-4 w-4" /> Log time
          </button>
          {periodNumber != null && (
            <Link to={`/timesheet/print/${periodNumber}`} target="_blank" className="tg-btn-secondary" data-testid="timesheet-print">
              <Printer className="h-4 w-4" /> Print PDF
            </Link>
          )}
        </div>
      </div>

      <Tabs value={view} onValueChange={setView} className="mb-4">
        <TabsList className="bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="day" className="rounded-lg" data-testid="view-day">Day</TabsTrigger>
          <TabsTrigger value="week" className="rounded-lg" data-testid="view-week">Week</TabsTrigger>
          <TabsTrigger value="2week" className="rounded-lg" data-testid="view-2week">2-Week</TabsTrigger>
        </TabsList>
      </Tabs>

      {view === "day" && (
        <div className="flex items-center gap-3 mb-4">
          <button
            className="h-9 w-9 rounded-xl border border-slate-200 hover:bg-slate-50 flex items-center justify-center"
            onClick={() => {
              const d = parseLocalDate(selectedDate);
              d.setDate(d.getDate() - 1);
              setSelectedDate(isoDate(d));
            }}
            data-testid="date-prev"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="tg-input max-w-[180px]"
            data-testid="date-input"
          />
          <button
            className="h-9 w-9 rounded-xl border border-slate-200 hover:bg-slate-50 flex items-center justify-center"
            onClick={() => {
              const d = parseLocalDate(selectedDate);
              d.setDate(d.getDate() + 1);
              setSelectedDate(isoDate(d));
            }}
            data-testid="date-next"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <div className="text-sm font-medium text-slate-500" data-testid="date-weekday">
            {weekdayShort(selectedDate)}
          </div>
          <div className="ml-auto text-sm text-slate-500">
            Total: <span className="font-bold text-slate-900 font-display">{formatHM(totalMin)}</span>
          </div>
        </div>
      )}

      {view === "week" && (
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <select
            value={weekValue}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="tg-input max-w-[320px]"
            data-testid="week-select"
          >
            {weekOptions.map((w) => (
              <option key={w.value} value={w.value}>
                Week {w.number} · {w.label}
              </option>
            ))}
          </select>
          <div className="text-sm text-slate-500">
            Total: <span className="font-bold text-slate-900 font-display">{formatHM(totalMin)}</span>
          </div>
        </div>
      )}

      {view === "2week" && periodInfo && (
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <select
            value={periodNumber || ""}
            onChange={(e) => setPeriodNumber(parseInt(e.target.value, 10))}
            className="tg-input w-full sm:w-auto sm:min-w-[460px]"
            data-testid="period-select"
          >
            {periodInfo.periods.map((p) => (
              <option key={p.number} value={p.number}>
                Period {p.number} · {weekdayShortYear(p.start)} → {weekdayShortYear(p.end)}
              </option>
            ))}
          </select>
          <div className="text-sm text-slate-500">
            Total: <span className="font-bold text-slate-900 font-display">{formatHM(totalMin)}</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {submission && <SubmissionBadge sub={submission} />}
            <button
              onClick={submit}
              disabled={!!submission && submission.status === "supervisor_approved"}
              className="tg-btn-primary disabled:opacity-50"
              data-testid="timesheet-submit"
            >
              <Send className="h-4 w-4" />
              {submission ? "Re-submit" : "Submit for approval"}
            </button>
          </div>
        </div>
      )}

      <div className="tg-card divide-y divide-slate-100">
        {filteredByView.length === 0 && (
          <div className="p-8 text-center text-slate-400 text-sm">
            No entries for this {view === "2week" ? "period" : view}. Tap "Log time" or talk to the AI:
            <span className="font-medium text-slate-600"> "I worked 2h on onboarding"</span>.
          </div>
        )}
        {filteredByView.map((a) => (
          <div key={a.id} className="px-5 py-3 flex items-center gap-3" data-testid={`activity-row-${a.id}`}>
            <div className="tg-stat-icon bg-teal-50 shrink-0">
              <Clock className="h-5 w-5 text-teal-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-800 truncate">{a.title}</div>
              <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span className="font-medium text-slate-600">{weekdayShort(a.date)}</span>
                {a.start_time && a.end_time && <span>· {a.start_time}–{a.end_time}</span>}
                {a.department_id && deptMap[a.department_id] && (
                  <span className="tg-pill bg-slate-100 text-slate-700 border-slate-200">{deptMap[a.department_id]}</span>
                )}
                {a.category_id && catMap[a.category_id] && (
                  <span className="tg-pill bg-emerald-50 text-emerald-700 border-emerald-200">{catMap[a.category_id].name}</span>
                )}
              </div>
            </div>
            <CompanyBadge entry={a} />
            <div className="text-sm font-bold text-slate-900 font-display w-12 text-right shrink-0">{formatHM(a.duration_minutes)}</div>
            <button
              onClick={() => setEditing(a)}
              className="h-8 w-8 rounded-lg hover:bg-emerald-50 text-slate-400 hover:text-emerald-600 flex items-center justify-center transition-colors"
              data-testid={`edit-activity-${a.id}`}
              title="Edit"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              onClick={() => del(a.id)}
              className="h-8 w-8 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center transition-colors"
              data-testid={`delete-activity-${a.id}`}
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      <EditActivityModal
        open={!!editing}
        onOpenChange={(o) => { if (!o) setEditing(null); }}
        activity={editing}
        onSaved={triggerRefresh}
      />

      {view === "2week" && submission && (
        <div className="tg-card p-5 mt-4" data-testid="submission-status">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Submission status</div>
          <ApprovalSteps sub={submission} />
        </div>
      )}

      {view === "2week" && submission?.approval_id && (
        <div className="tg-card p-5 mt-4" data-testid="timesheet-thread">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
            Discussion with your supervisor
          </div>
          <p className="text-xs text-slate-400 mb-3">
            Questions about this timesheet? Message your supervisor here — they see the same thread when reviewing.
          </p>
          <div className="h-80">
            <Thread objectType="approval" objectId={submission.approval_id} />
          </div>
        </div>
      )}
    </div>
  );
}

function SubmissionBadge({ sub }) {
  const map = {
    employee_approved: { text: "Awaiting supervisor", c: "bg-amber-50 text-amber-700 border-amber-200" },
    supervisor_approved: { text: "Approved", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    rejected: { text: "Rejected", c: "bg-rose-50 text-rose-700 border-rose-200" },
  };
  const m = map[sub.status] || { text: sub.status, c: "bg-slate-100 text-slate-700 border-slate-200" };
  return <span className={`tg-pill ${m.c}`}>{m.text}</span>;
}

function ApprovalSteps({ sub }) {
  const Step = ({ done, label, sub: s }) => (
    <div className="flex items-center gap-2">
      <div className={`h-7 w-7 rounded-full flex items-center justify-center ${done ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400"}`}>
        <CheckCircle2 className="h-4 w-4" />
      </div>
      <div>
        <div className="text-sm font-semibold text-slate-800">{label}</div>
        {s && <div className="text-xs text-slate-500">{s}</div>}
      </div>
    </div>
  );
  const rejected = sub.status === "rejected";
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <Step done={true} label="Employee approved" sub={sub.employee_approved_at?.slice(0, 16).replace("T", " ")} />
      <div className="h-px w-10 bg-slate-200 hidden sm:block" />
      <Step
        done={sub.status === "supervisor_approved"}
        label={rejected ? "Supervisor rejected" : "Supervisor approval"}
        sub={sub.supervisor_approved_at?.slice(0, 16).replace("T", " ") || (rejected ? sub.rejection_note : "Pending")}
      />
      {rejected && (
        <div className="ml-auto text-sm text-rose-700 flex items-center gap-1">
          <AlertCircle className="h-4 w-4" />
          {sub.rejection_note || "Rejected"}
        </div>
      )}
    </div>
  );
}
